// src/App.tsx
import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { LanguageProvider } from "./context/LanguageContext";
import { ThemeProvider } from "./context/ThemeContext";
import { C } from "./theme";

import ProtectedRoute from "./components/ProtectedRoute";
import LandingPage from "./pages/LandingPage";
import PublicEvents from "./pages/PublicEvents";
import PublicCompetitionRegistrations from "./pages/PublicCompetitionRegistrations";
import PublicCompetitionDraws from "./pages/PublicCompetitionDraws";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import PublicAthletes from "./pages/PublicAthletes";
import PublicClubs from "./pages/PublicClubs";
import PublicClubProfile from "./pages/PublicClubProfile";
import Dashboard from "./pages/Dashboard";
import Register from "./pages/Register";
import PaymentFailed from "./pages/PaymentFailed";
import PaymentSuccess from "./pages/PaymentSuccess";
import RegistrationSuccess from "./pages/RegistrationSuccess";
import CoachHistory from "./pages/CoachHistory";
import CoachClub from "./pages/CoachClub";
import AdminDashboard from "./pages/admin/AdminDashboard";
import ManageAthletes from "./pages/admin/ManageAthletes";
import ManageClubs from "./pages/admin/ManageClubs";
import ManageCompetitions from "./pages/admin/ManageCompetitions";
import AdminRegistrations from "./pages/admin/AdminRegistrations";
import AdminRecentRegistrations from "./pages/admin/AdminRecentRegistrations";
import ViewRegistrations from "./pages/admin/ViewRegistrations";
import AdminCoaches from "./pages/admin/AdminCoaches";
import ManageRankings from "./pages/admin/ManageRankings";
import ManageCreditPackages from "./pages/admin/ManageCreditPackages";
import AuditHistory from "./pages/admin/AuditHistory";
import RankingsPage from "./pages/rankings/RankingsPage";
import AthleteProfile from "./pages/rankings/AthleteProfile";
import ClubRankings from "./pages/rankings/ClubRankings";
import Footer from "./components/Footer";

const TournamentDrawLab = React.lazy(() => import("./pages/admin/TournamentDrawLab"));
const AffiliationReconciliation = React.lazy(() => import("./pages/admin/AffiliationReconciliation"));
const RankingWorkflow = React.lazy(() => import("./pages/admin/RankingWorkflow"));

// ── /success route dispatcher ─────────────────────────────────────────────
//   - Prepaid / admin-manual flows reach /success via navigate("/success",
//     { state: { referenceCode, athletes, ... } }) — render the full receipt.
//   - BOG redirects to /success with no React state — render PaymentSuccess,
//     which reads pendingOrderId from sessionStorage and shows the
//     bilingual confirmation + auto-redirect to /history.
function SuccessDispatcher() {
  const location = useLocation();
  return location.state ? <RegistrationSuccess /> : <PaymentSuccess />;
}

// ── Background wrapper (only applies to authenticated pages) ─────────────────
const AppBackground = ({ children }: { children: React.ReactNode }) => {
  const { currentUser } = useAuth();
  void currentUser; // Silence unused variable warning

  return (
    <div style={s.appBackground}>
      <div style={s.backgroundLeft}>
        <img src="/geo.png" alt="Logo" style={s.backgroundLogo} />
      </div>
      <div style={s.backgroundRight} />
      <div style={s.contentWrapper}>
        <div style={{ flex: 1 }}>
          {children}
        </div>
        <Footer />
      </div>
    </div>
  );
};

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <ThemeProvider>
          <LanguageProvider>
            <AuthProvider>
              <AppBackground>
              <Routes>
                {/* ── Public routes */}
                <Route path="/" element={<LandingPage />} />
                <Route path="/login" element={<LandingPage />} />
                <Route path="/events" element={<PublicEvents />} />
                <Route path="/competition/:id/registrations" element={<PublicCompetitionRegistrations />} />
                <Route path="/competition/:id/draws" element={<PublicCompetitionDraws />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/athletes" element={<PublicAthletes />} />
                <Route path="/athletes/:athleteSlug" element={<AthleteProfile />} />
                <Route path="/clubs" element={<PublicClubs />} />
                <Route path="/clubs/:clubSlug" element={<PublicClubProfile />} />

                {/* ── Public rankings routes */}
                <Route path="/rankings" element={<RankingsPage />} />
                <Route path="/rankings/athlete/:athleteId" element={<AthleteProfile />} />
                <Route path="/rankings/clubs" element={<ClubRankings />} />

                {/* ── Coach routes */}
                <Route element={<ProtectedRoute allowedRoles={["coach"]} />}>
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/register/:competitionId" element={<Register />} />
                  <Route path="/success" element={<SuccessDispatcher />} />
                  <Route path="/payment-failed" element={<PaymentFailed />} />
                  <Route path="/history" element={<CoachHistory />} />
                  <Route path="/my-club" element={<CoachClub />} />
                </Route>

                {/* ── Shared admin + ranking_admin routes */}
                <Route element={<ProtectedRoute allowedRoles={["admin", "ranking_admin"]} />}>
                  <Route path="/admin" element={<AdminDashboard />} />
                  <Route path="/admin/athletes" element={<ManageAthletes />} />
                  <Route path="/admin/clubs" element={<ManageClubs />} />
                  <Route path="/admin/competitions" element={<ManageCompetitions />} />
                  <Route path="/admin/rankings" element={<ManageRankings />} />
                  <Route path="/admin/rankings/workflow" element={<React.Suspense fallback={<div style={s.routeLoading}>Loading ranking workflow…</div>}><RankingWorkflow /></React.Suspense>} />
                  <Route
                    path="/admin/draw-lab"
                    element={
                      <React.Suspense fallback={<div style={s.routeLoading}>Loading draw tools…</div>}>
                        <TournamentDrawLab />
                      </React.Suspense>
                    }
                  />
                </Route>

                {/* ── Admin-only sensitive routes */}
                <Route element={<ProtectedRoute allowedRoles={["admin"]} />}>
                  <Route
                    path="/admin/affiliations"
                    element={
                      <React.Suspense fallback={<div style={s.routeLoading}>Loading affiliation tools…</div>}>
                        <AffiliationReconciliation />
                      </React.Suspense>
                    }
                  />
                  <Route path="/admin/registrations" element={<AdminRegistrations />} />
                  <Route
                    path="/admin/registrations/:competitionId"
                    element={<ViewRegistrations />}
                  />
                  <Route
                    path="/admin/recent-registrations"
                    element={<AdminRecentRegistrations />}
                  />
                  <Route path="/admin/coaches" element={<AdminCoaches />} />
                  <Route path="/admin/credits" element={<ManageCreditPackages />} />
                  <Route path="/admin/audit" element={<AuditHistory />} />
                </Route>

                {/* ── Catch-all */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
              </AppBackground>
            </AuthProvider>
          </LanguageProvider>
        </ThemeProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

const s: Record<string, React.CSSProperties> = {
  routeLoading: {
    display: "grid",
    minHeight: "55vh",
    placeItems: "center",
    color: C.text2,
    fontSize: "14px",
  },
  appBackground: {
    position: "relative",
    minHeight: "100vh",
    backgroundColor: C.bg,
    overflow: "hidden",
  },
  backgroundLeft: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    backgroundColor: C.bg,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 0,
  },
  backgroundLogo: {
    width: "50%",
    maxWidth: "400px",
    height: "auto",
    objectFit: "contain",
    opacity: 0.05,
  },
  backgroundRight: {
    position: "fixed",
    top: 0,
    right: 0,
    width: "100%",
    height: "100%",
    backgroundColor: "transparent",
    zIndex: 0,
  },
  contentWrapper: {
    position: "relative",
    zIndex: 1,
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
  },
};

export default App;
